import React, { useEffect, useState, useCallback } from "react";
import {
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Tooltip, LinearProgress, Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";
import AddIcon from "@material-ui/icons/Add";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";

const useStyles = makeStyles(theme => ({
  period: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  progressLabel: { fontSize: 11, color: theme.palette.text.secondary, fontVariantNumeric: "tabular-nums" },
  filters: { display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" },
  progress: { height: 10, borderRadius: 5 },
  cell: { fontSize: 13 }
}));

const METRICS = [
  { value: "deals_won_count", label: "Negócios Ganhos (qtd)" },
  { value: "deals_won_amount", label: "Negócios Ganhos (R$)" },
  { value: "revenue_paid", label: "Receita Recebida" },
  { value: "tasks_done_count", label: "Tarefas Concluídas" },
  { value: "tickets_closed", label: "Tickets Fechados" }
];

const SCOPES = [
  { value: "company", label: "Empresa" },
  { value: "user", label: "Usuário" },
  { value: "team", label: "Equipe" }
];

const STATUS = [
  { value: "active", label: "Ativa" },
  { value: "achieved", label: "Atingida" },
  { value: "expired", label: "Expirada" },
  { value: "archived", label: "Arquivada" }
];

const PERIODS = [
  { value: "monthly", label: "Mensal" },
  { value: "quarterly", label: "Trimestral" },
  { value: "yearly", label: "Anual" },
  { value: "custom", label: "Personalizado" }
];

const Goals = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState({ status: "", scope: "", search: "" });
  const [dlg, setDlg] = useState({ open: false, editing: null, form: {} });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.scope) params.scope = filter.scope;
      if (filter.search) params.search = filter.search;
      const { data } = await api.get("/goals", { params });
      setList(data || []);
    } catch (err) {
      toast.error("Falha ao carregar metas");
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data?.users || data || [])).catch(() => {});
  }, []);

  const open = (g = null) => {
    setDlg({
      open: true,
      editing: g,
      form: g ? { ...g } : {
        name: "", description: "", type: "sales", metric: "deals_won_amount",
        period: "monthly", targetValue: 0, status: "active", scope: "company",
        userId: "", startDate: new Date().toISOString().slice(0, 10),
        endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().slice(0, 10)
      }
    });
  };
  const close = () => setDlg({ open: false, editing: null, form: {} });

  const save = async () => {
    try {
      const payload = { ...dlg.form };
      if (payload.scope !== "user") payload.userId = null;
      if (dlg.editing) await api.put(`/goals/${dlg.editing.id}`, payload);
      else await api.post("/goals", payload);
      toast.success("Meta salva");
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao salvar");
    }
  };

  const remove = async id => {
    if (!(await confirm({ title: "Excluir esta meta?", message: "O histórico de progresso é perdido junto." }))) return;
    try {
      await api.delete(`/goals/${id}`);
      toast.success("Excluída");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const recalc = async id => {
    try {
      await api.post(`/goals/${id}/recalc`);
      toast.success("Recalculada");
      load();
    } catch {
      toast.error("Erro ao recalcular");
    }
  };

  const recalcAll = async () => {
    try {
      const { data } = await api.post("/goals/recalc-all");
      toast.success(`${data.recalculated} metas atualizadas`);
      load();
    } catch {
      toast.error("Erro ao recalcular");
    }
  };

  const setF = (k, v) => setDlg(d => ({ ...d, form: { ...d.form, [k]: v } }));
  const fmt = v => Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });

  const columns = [
    { key: "name", label: "Nome", render: g => <Typography variant="subtitle2">{g.name}</Typography> },
    {
      key: "metric",
      label: "Métrica",
      render: g => METRICS.find(m => m.value === g.metric)?.label || g.metric,
    },
    {
      key: "period",
      label: "Período",
      render: g => (
        <span className={classes.period}>
          {g.startDate} → {g.endDate}
        </span>
      ),
    },
    {
      key: "scope",
      label: "Escopo",
      render: g =>
        g.scope === "user"
          ? `Usuário: ${g.user?.name || "-"}`
          : SCOPES.find(s => s.value === g.scope)?.label,
    },
    {
      key: "progress",
      label: "Progresso",
      width: 220,
      render: g => {
        const target = Number(g.targetValue) || 0;
        const value = Number(g.currentValue) || 0;
        const pct = target > 0 ? Math.min(100, (value / target) * 100) : 0;
        return (
          <div>
            <LinearProgress variant="determinate" value={pct} className={classes.progress} />
            <span className={classes.progressLabel}>
              {fmt(value)} / {fmt(target)} ({pct.toFixed(1)}%)
            </span>
          </div>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      render: g => {
        const meta = STATUS.find(s => s.value === g.status);
        const tone =
          g.status === "achieved" ? "success"
          : g.status === "failed" ? "danger"
          : g.status === "active" ? "info"
          : "neutral";
        return <StatusChip dot tone={tone} label={meta?.label || g.status} />;
      },
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: g => (
        <>
          <Tooltip title="Recalcular o progresso desta meta">
            <IconButton size="small" onClick={() => recalc(g)}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => open(g)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" onClick={() => remove(g)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title="Metas"
        count={list.length}
        subtitle="Objetivos com período e valor-alvo. O progresso é recalculado a partir dos dados do CRM."
        actions={
          <>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={recalcAll}>
              Recalcular todas
            </Button>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => open()}>
              Nova meta
            </Button>
          </>
        }
        filters={
          <>
            <TextField label="Buscar" value={filter.search}
              onChange={e => setFilter(f => ({ ...f, search: e.target.value }))} />
            <TextField select label="Status" value={filter.status} style={{ minWidth: 150 }}
              onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
              <MenuItem value="">Todos</MenuItem>
              {STATUS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            <TextField select label="Escopo" value={filter.scope} style={{ minWidth: 150 }}
              onChange={e => setFilter(f => ({ ...f, scope: e.target.value }))}>
              <MenuItem value="">Todos</MenuItem>
              {SCOPES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={list}
        emptyIcon={<EmojiEventsIcon />}
        emptyTitle="Nenhuma meta cadastrada"
        emptyDescription="Defina metas de vendas, atendimento ou financeiro para acompanhar o progresso da equipe."
        emptyAction={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => open()}>
            Nova meta
          </Button>
        }
      />

      <Dialog open={dlg.open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{dlg.editing ? "Editar Meta" : "Nova Meta"}</DialogTitle>
        <DialogContent>
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr", paddingTop: 8 }}>
            <TextField label="Nome" value={dlg.form.name || ""} onChange={e => setF("name", e.target.value)}
              style={{ gridColumn: "1 / span 2" }} required />
            <TextField label="Descrição" value={dlg.form.description || ""}
              onChange={e => setF("description", e.target.value)} multiline rows={2}
              style={{ gridColumn: "1 / span 2" }} />
            <TextField select label="Métrica" value={dlg.form.metric || ""} onChange={e => setF("metric", e.target.value)}>
              {METRICS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
            <TextField label="Valor Alvo" type="number" value={dlg.form.targetValue || 0}
              onChange={e => setF("targetValue", Number(e.target.value))} />
            <TextField select label="Período" value={dlg.form.period || ""} onChange={e => setF("period", e.target.value)}>
              {PERIODS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </TextField>
            <TextField select label="Status" value={dlg.form.status || "active"} onChange={e => setF("status", e.target.value)}>
              {STATUS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            <TextField select label="Escopo" value={dlg.form.scope || "company"} onChange={e => setF("scope", e.target.value)}>
              {SCOPES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            <TextField select label="Usuário" value={dlg.form.userId || ""}
              onChange={e => setF("userId", e.target.value)} disabled={dlg.form.scope !== "user"}>
              <MenuItem value="">—</MenuItem>
              {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
            </TextField>
            <TextField label="Início" type="date" InputLabelProps={{ shrink: true }}
              value={dlg.form.startDate || ""} onChange={e => setF("startDate", e.target.value)} />
            <TextField label="Fim" type="date" InputLabelProps={{ shrink: true }}
              value={dlg.form.endDate || ""} onChange={e => setF("endDate", e.target.value)} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={save}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Goals;
